import { getHanziScriptConvertersQueryOptions } from "@-/dev.improve.haniverse.lib.hanzi-script/query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { type HanziScriptConverters } from "@-/dev.improve.haniverse.lib.hanzi-script";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";

export type SentenceExample = {
  id: number;
  sentence: string;
  translation: string | null;
};

/**
 * Normalize a source sentence into the reader's script.
 *
 * Tatoeba's Mandarin corpus is genuinely MIXED and carries no per-sentence script label — a
 * 20k-row sample is ~51% traditional, ~39% simplified, ~7% script-neutral. So there is no
 * single "source script" to convert from; each sentence must be normalized *into* the target
 * script regardless of what it started as. Converting simplified→traditional text is a no-op
 * for characters already in the target script, so a one-way convert is safe to apply
 * unconditionally.
 *
 * This is the same normalization the build previously did with `t2cn()`/`cn2t()`, just applied
 * at display time against one stored row instead of baked into two.
 */
function toReaderScript(
  sentence: string,
  hanziScript: HanziScript,
  converters: HanziScriptConverters,
): string {
  return hanziScript === "traditional"
    ? converters.simplifiedToTraditional(sentence)
    : converters.traditionalToSimplified(sentence);
}

/** Default cap on how many example sentences we surface for a single card front. */
export const SENTENCE_EXAMPLES_LIMIT = 20;

export function useGetSentenceExamplesQueryKey() {
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const withTableChangeVersion = useActorTableChangeVersion("user", {});

  // A null limit is reserved for the virtualized all-examples browser.
  return (front: string, limit: number | null = SENTENCE_EXAMPLES_LIMIT) =>
    withTableChangeVersion([
      "sentenceExamples",
      front,
      limit,
      { actorUserDataQueryOptions, staticDb, traditionalCharactersEnabled },
    ] as const);
}

export const [useSentenceExamplesQueryOptions, sentenceExamplesQueryFn] = createUseQueryOptions(
  useGetSentenceExamplesQueryKey,
  async ({
    client,
    queryKey: [
      _,
      front,
      limit,
      { actorUserDataQueryOptions, staticDb, traditionalCharactersEnabled },
    ],
  }) => {
    const actorUserData = await client.fetchQuery(actorUserDataQueryOptions);
    const hanziScript = availableHanziScript(
      actorUserData.user.hanziScript,
      traditionalCharactersEnabled,
    );
    if (front.length === 0) {
      return [];
    }

    // Converters are loaded lazily and cached with `staleTime: Infinity`, so this costs one
    // dynamic import per session rather than per sentence.
    const converters = await client.fetchQuery(getHanziScriptConvertersQueryOptions());

    // Sentences are stored in their SOURCE form — there is no `hanzi_script` column, because
    // the corpus carries no per-sentence script label (and is genuinely mixed: ~51%
    // traditional, ~39% simplified in a 20k sample). So a single-form substring match would
    // silently miss every example written in the other script. Search both forms of the front
    // and let the DB dedupe by id.
    const simplifiedFront = converters.traditionalToSimplified(front);
    const traditionalFront = converters.simplifiedToTraditional(front);
    const fronts =
      simplifiedFront === traditionalFront
        ? [simplifiedFront]
        : [simplifiedFront, traditionalFront];

    const rows = await staticDb(import.meta.location).query.sentence.findMany({
      columns: { id: true, sentence: true, translation: true },
      limit: limit ?? undefined,
      orderBy: (sentence, { asc, sql }) => [
        asc(sql`length(${sentence.sentence})`),
        asc(sentence.id),
      ],
      where: { OR: fronts.map((form) => ({ sentence: { like: `%${form}%` } })) },
    });
    return rows.map((row) => ({
      id: row.id,
      sentence: toReaderScript(row.sentence, hanziScript, converters),
      translation: row.translation,
    }));
  },
);
